import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import MapView from '@arcgis/core/views/MapView';
import Sketch from '@arcgis/core/widgets/Sketch';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import SketchViewModel from '@arcgis/core/widgets/Sketch/SketchViewModel';
import Color from '@arcgis/core/Color';
import Graphic from '@arcgis/core/Graphic';
import { BehaviorSubject } from 'rxjs';
import SimpleFillSymbol from '@arcgis/core/symbols/SimpleFillSymbol';
import SpatialReference from '@arcgis/core/geometry/SpatialReference';
import MapViewScreenPoint = __esri.MapViewScreenPoint;

import { EsriHelpers } from '../../_helpers/EsriHelpers';
import { ISagtFeature } from '../../_models/SagtFeature';
import { CodedValue } from '../../_models/models';
import { ILayerFilterConfig } from 'src/app/_models/ApplicationConfig';

@Component({
  selector: 'app-map-edition-geometry',
  templateUrl: './map-edition-geometry.component.html',
  styleUrls: ['./map-edition-geometry.component.scss'],
})
export class MapEditionGeometryComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() height = 400;
  @Input() webMapId!: string;
  @Input() defaultPointSymbol: any;
  @Input() defaultPolylineSymbol: any;
  @Input() defaultPolygonSymbol: any;
  @Input() codedValueForPolyline!: BehaviorSubject<CodedValue | undefined>;
  @Input() graphics: Graphic[] | undefined;
  @Input() readonlyMode = false;
  @Input() filterLayer: ILayerFilterConfig | null = null;
  @Output() displayCodedValueForPolyline = new EventEmitter<{
    screenPoint: MapViewScreenPoint;
    ids: number[];
  }>();
  @Output() featuresChange = new EventEmitter<ISagtFeature[]>();
  @ViewChild('mapViewNode', { static: true }) mapViewNode!: ElementRef;

  public mapLoading = true;
  public selectedColor!: string;

  private mapView!: MapView;
  private sketch!: Sketch;
  private graphicsLayer = new GraphicsLayer();
  private currentEditedGraphic?: Graphic;

  constructor() {}

  ngOnInit(): void {
    if (this.codedValueForPolyline) {
      this.codedValueForPolyline.subscribe((c) => {
        if (c) {
          console.debug(`composante selected => id`, c.code);
          if (this.currentEditedGraphic) {
            this.currentEditedGraphic.attributes = {
              idComposante: c.code,
            };
          }
          this.updateFeaturesInForm();
          this.displayCodedValueForPolyline.emit();
        }
      });
    }
  }

  ngAfterViewInit(): void {
    this.initializeMap()
      .then(() => {
        if (!this.readonlyMode) {
          this.initializeDraw();
        } else {
          this.mapLoading = false;
        }
      })
      .catch(() => {});
  }

  ngOnDestroy(): void {
    if (this.mapView) {
      this.mapView.destroy();
    }
  }

  private async initializeMap(): Promise<void> {
    try {
      this.graphicsLayer.set('spatialReference', new SpatialReference({ wkid: 2056 }));

      this.mapView = await EsriHelpers.GenerateMapAsync(this.mapViewNode.nativeElement, this.webMapId, [this.graphicsLayer], {
        popup: { autoOpenEnabled: false },
        activateIdentify: false,
      });

      if (this.graphics) {
        if (!this.readonlyMode) {
          console.debug(this.graphics);
          this.graphicsLayer.graphics.addMany(this.graphics);
        }
        this.mapView.goTo(this.graphics);
      }

      if (this.filterLayer?.definitionExpression) {
        const layer = this.mapView.map.layers.find(
          (x) => x.type === 'map-image' && (x as __esri.MapImageLayer).url.toLocaleLowerCase() === this.filterLayer!.url.toLocaleLowerCase()
        ) as __esri.MapImageLayer;
        if (layer?.sublayers?.length) {
          layer.sublayers.forEach((sl) => {
            if (this.filterLayer!.layerIds.includes(sl.id)) {
              sl.definitionExpression = this.filterLayer!.definitionExpression as string;
            }
          });
        }
      }

      this.handleEditionOfAttributesForPolyline();
    } catch (error: any) {
      console.error(error.message);
    }
  }

  private handleEditionOfAttributesForPolyline(): void {
    if (this.codedValueForPolyline && !this.readonlyMode) {
      this.mapView.on('click', (evt) => {
        if (evt.native.button === 2 || evt.native.button === 3) {
          this.mapView.hitTest(evt).then((res) => {
            for (const result of res.results) {
              // @ts-ignore
              if (result.graphic.geometry.type === 'polyline') {
                this.currentEditedGraphic = (result as any).graphic as Graphic;
                const screenPoint = this.mapView.toScreen(this.currentEditedGraphic.geometry.extent.center);
                this.displayCodedValueForPolyline.emit({
                  screenPoint,
                  ids: [this.currentEditedGraphic.attributes.idComposante],
                });
                break;
              }
            }
          });
        }
      });
    }
  }

  private initializeDraw(): void {
    if (!this.mapView) {
      return;
    }

    this.sketch = new Sketch({
      view: this.mapView,
      layer: this.graphicsLayer,
      viewModel: new SketchViewModel({
        pointSymbol: this.defaultPointSymbol || EsriHelpers.defaultPointSymbol,
        polylineSymbol: this.defaultPolylineSymbol || EsriHelpers.defaultPolylineSymbol,
        polygonSymbol: this.defaultPolygonSymbol || EsriHelpers.defaultPolygonSymbol,
        layer: this.graphicsLayer,
        view: this.mapView,
      }),
    });

    this.sketch.on('create', (event) => {
      if (event.graphic && event.graphic.geometry.type === 'polyline') {
        if (event.state === 'complete') {
          this.currentEditedGraphic = event.graphic;

          if (this.codedValueForPolyline) {
            const screenPoint = this.mapView.toScreen(event.graphic.geometry.extent.center);
            this.displayCodedValueForPolyline.emit({ screenPoint, ids: [] });
          } else {
            this.updateFeaturesInForm();
          }
        }
      } else {
        if (event.state === 'complete') {
          this.updateFeaturesInForm();
        }
      }
    });
    this.sketch.on('update', (event) => {
      this.selectedColor = event.graphics[0].symbol.color.toCss(true);
      if (event.state === 'complete') {
        this.updateFeaturesInForm();
      }
    });
    this.sketch.on('delete', () => {
      this.updateFeaturesInForm();
    });
    this.sketch.on('undo', () => {
      this.updateFeaturesInForm();
    });
    this.sketch.on('redo', () => {
      this.updateFeaturesInForm();
    });

    this.mapView.ui.add(this.sketch, 'top-right');
    this.initializeColorPicker();

    this.mapLoading = false;
  }

  private updateFeaturesInForm(): void {
    console.debug(this.graphicsLayer.graphics);
    this.featuresChange.emit(this.graphicsLayer.graphics.map((g) => EsriHelpers.GraphicToSagtFeature(g)).toArray());
  }

  private initializeColorPicker(): void {
    const elem = document.getElementById('pgmDialogEditColorPickerComponent');
    if (elem) {
      this.mapView.ui.add(elem, 'top-right');
    }
  }

  colorChange(event: any): void {
    this.selectedColor = event;
    this.sketch.viewModel.polygonSymbol.color = Color.fromArray(EsriHelpers.rgbaToArray(this.selectedColor));

    if (this.sketch.updateGraphics.length) {
      for (const graphic of this.sketch.updateGraphics) {
        if (graphic.geometry.type === 'polygon') {
          const graphicToEdit = this.graphicsLayer.graphics.find((x) => x.get('uid') === graphic.get('uid'));
          const symbol = {
            type: 'simple-fill',
            color: this.sketch.viewModel.polygonSymbol.color,
            outline: (graphicToEdit.symbol as SimpleFillSymbol).outline,
            style: (graphicToEdit.symbol as SimpleFillSymbol).style,
          };
          graphicToEdit.set('symbol', symbol);
          this.sketch.update(graphicToEdit);
        }
      }
    }
  }
}
